import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel, LiveTag } from "../components/common/Panel";
import { getGovernance, proposeValidator, discardValidator, getGovTimeline } from "../lib/api";
import { shorten, timeAgo } from "../lib/format";
import { useAdmin } from "../hooks/useAdmin";

const ACTION_META = {
  add: { label: "PROPOSE ADD", cls: "pill-green", icon: "fa-user-plus" },
  remove: { label: "PROPOSE REMOVE", cls: "pill-red", icon: "fa-user-minus" },
  discard: { label: "DISCARD VOTE", cls: "pill-muted", icon: "fa-ban" },
};

export default function Governance() {
  const { admin } = useAdmin();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [addr, setAddr] = useState("");
  const [action, setAction] = useState("add");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    getGovernance().then(setData).catch(() => {});
    getGovTimeline().then((d) => setTimeline(d.events || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  const valid = /^0x[a-fA-F0-9]{40}$/.test(addr.trim());

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!valid) { setError("Enter a valid validator address (0x…40 hex)."); return; }
    setBusy(true);
    try {
      const r = await proposeValidator(addr.trim(), action === "add");
      setResult(r);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Proposal failed");
    } finally { setBusy(false); }
  };

  const discard = async (a) => {
    try { await discardValidator(a); load(); } catch {}
  };

  const vals = data?.validators || [];
  const pending = data?.pending || [];

  return (
    <div className="fade-in" data-testid="governance-page">
      <div className="mb-4">
        <span className="eyebrow" style={{ color: "var(--amber)" }}>Consensus Admin</span>
        <h1 className="font-display text-[24px] font-extrabold mt-1">Validator Governance</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>
          Propose adding or removing QBFT validators. A change is enacted once a majority (&ge;{data?.threshold ?? 3} of {vals.length || 4}) of current validators vote the same way.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Active Validator Set" action={<LiveTag />} bodyClass="overflow-auto" testId="panel-gov-validators">
          <table className="dt">
            <thead><tr><th>#</th><th>Validator</th><th>Status</th></tr></thead>
            <tbody>
              {vals.map((v, i) => (
                <tr key={v} data-testid={`gov-validator-${i}`}>
                  <td className="mono" style={{ color: "var(--muted)" }}>{i + 1}</td>
                  <td><Link to={`/address/${v}`} className="link-amber mono text-[12px]">{v}</Link></td>
                  <td><span className="pill pill-green"><span className="dot dot-green live-dot" /> Active</span></td>
                </tr>
              ))}
              {vals.length === 0 && <tr><td colSpan={3} className="text-center py-8" style={{ color: "var(--muted)" }}>Loading…</td></tr>}
            </tbody>
          </table>
        </Panel>

        <div className="flex flex-col gap-5">
          <Panel title="Propose Change" action={admin ? <span className="pill pill-green"><i className="fa-solid fa-user-shield" /> Admin</span> : null} testId="panel-propose">
            {!admin ? (
              <div className="p-6 text-center" data-testid="propose-locked">
                <i className="fa-solid fa-lock text-[22px] mb-3" style={{ color: "var(--muted-2)" }} />
                <p className="text-[13px]" style={{ color: "var(--muted)" }}>Proposing validator changes requires admin access.</p>
                <Link to="/admin" className="btn-amber inline-block mt-3 py-2 px-5 text-[12px]" data-testid="propose-login-link">Sign in to Admin Console</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5">
                <div className="flex gap-2 mb-3">
                  <button type="button" className={`nav-link flex-1 ${action === "add" ? "active" : ""}`} onClick={() => setAction("add")} data-testid="action-add">
                    <i className="fa-solid fa-user-plus mr-1" /> Add Validator
                  </button>
                  <button type="button" className={`nav-link flex-1 ${action === "remove" ? "active" : ""}`} onClick={() => setAction("remove")} data-testid="action-remove">
                    <i className="fa-solid fa-user-minus mr-1" /> Remove Validator
                  </button>
                </div>
                <label className="eyebrow block mb-2">Validator Address</label>
                <input className="input-dark mb-3" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x…" data-testid="gov-address-input" />
                <button type="submit" className="btn-amber w-full" disabled={busy || !valid} data-testid="gov-submit-btn">
                  {busy ? "Broadcasting vote…" : `Propose to ${action === "add" ? "Add" : "Remove"}`}
                </button>
                {error && <div className="mt-3 text-[12px] p-2 rounded" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="gov-error">{error}</div>}
                {result && (
                  <div className="mt-3 text-[12.5px] p-3 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)" }} data-testid="gov-success">
                    <div style={{ color: "var(--green)" }}><i className="fa-solid fa-circle-check mr-1" /> Vote broadcast to {result.nodesVoted}/{result.total} validator nodes.</div>
                    <div className="mt-1" style={{ color: "var(--muted)" }}>Once {data?.threshold} validators agree, {shorten(result.address, 6, 4)} will be {result.add ? "added" : "removed"}.</div>
                  </div>
                )}
              </form>
            )}
          </Panel>

          <Panel title="Pending Votes" action={<span className="pill pill-muted">{pending.length}</span>} testId="panel-pending-votes">
            <div className="p-2">
              {pending.map((p) => (
                <div key={p.address} className="flex items-center justify-between px-3 py-3" style={{ borderBottom: "1px solid rgba(30,40,49,0.5)" }} data-testid={`pending-${p.address.slice(0,8)}`}>
                  <div className="flex items-center gap-2">
                    <span className={`pill ${p.auth ? "pill-green" : "pill-red"}`}>{p.auth ? "ADD" : "REMOVE"}</span>
                    <Link to={`/address/${p.address}`} className="link-amber mono text-[12px]">{shorten(p.address, 6, 4)}</Link>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="mono text-[11.5px]" style={{ color: "var(--muted)" }}>{p.votes}/{data?.threshold} votes</span>
                    {admin && <button className="link-amber text-[11.5px]" onClick={() => discard(p.address)} data-testid={`discard-${p.address.slice(0,8)}`}>discard</button>}
                  </div>
                </div>
              ))}
              {pending.length === 0 && <div className="text-center py-6 text-[13px]" style={{ color: "var(--muted)" }}>No pending validator votes.</div>}
            </div>
          </Panel>
        </div>
      </div>

      {/* Governance timeline */}
      <Panel title="Governance Timeline" subtitle="decision history" action={<LiveTag />} className="mt-5" testId="panel-gov-timeline">
        <div className="p-4">
          {timeline.length === 0 && <div className="text-center py-8 text-[13px]" style={{ color: "var(--muted)" }}>No governance actions yet.</div>}
          <div className="space-y-0">
            {timeline.map((ev, i) => {
              const meta = ACTION_META[ev.action] || ACTION_META.discard;
              return (
                <div key={i} className="flex items-start gap-3 py-3" style={{ borderBottom: i < timeline.length - 1 ? "1px solid rgba(30,40,49,0.5)" : "none" }} data-testid={`timeline-event-${i}`}>
                  <div className="mt-0.5 flex items-center justify-center rounded-full shrink-0" style={{ width: 30, height: 30, background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
                    <i className={`fa-solid ${meta.icon}`} style={{ color: "var(--amber)", fontSize: 12 }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill ${meta.cls}`}>{meta.label}</span>
                      <Link to={`/address/${ev.address}`} className="link-amber mono text-[12px]">{shorten(ev.address, 8, 6)}</Link>
                    </div>
                    <div className="text-[11.5px] mt-1" style={{ color: "var(--muted)" }}>
                      by <span className="mono">{ev.by || "system"}</span>
                      {typeof ev.nodesVoted === "number" && <> · broadcast to {ev.nodesVoted} node{ev.nodesVoted === 1 ? "" : "s"}</>}
                    </div>
                  </div>
                  <span className="mono text-[11px] shrink-0" style={{ color: "var(--muted-2)" }}>{ev.ts ? timeAgo(ev.ts) : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Panel>
    </div>
  );
}
